import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { settingTypes } from '@ipfabric/utils';
import type { VendorApiType } from '@ipfabric/utils/settings/vendorApi/names';
import { Button } from 'components/Button/Button';
import { pipe } from 'fp-ts/function';
import { produce } from 'immer';
import React, { type FC, useState } from 'react';
import {
  ArubaNewCentralForm,
  type ArubaNewCentralFormValues,
} from './ArubaNewCentralForm';
import { AwsForm, type AwsFormValues } from './AwsForm';
import { AzureForm, type AzureFormValues } from './AzureForm';
import {
  type CheckPointFormValues,
  CheckpointApiForm,
} from './CheckpointApiForm';
import { CiscoApicForm, type CiscoApicFormValues } from './CiscoApicForm';
import { CiscoFmcForm, type CiscoFmcFormValues } from './CiscoFmcForm';
import { F5osAForm, type F5osAFormValues } from './F5osAForm';
import { F5osCForm, type F5osCFormValues } from './F5osCForm';
import { ForcePointForm, type ForcePointFormValues } from './ForcePointForm';
import {
  type GCPFormValues,
  GoogleCloudPlatformForm,
} from './GoogleCloudPlatformForm';
import { JuniperMistForm, type JuniperMistFormValues } from './JuniperMistForm';
import { MerakiForm, type MerakiFormValues } from './MerakiForm';
import { NsxTForm, type NsxTFormValues } from './NsxTForm';
import { PrismaForm, type PrismaFormValues } from './PrismaForm';
import { RuckusForm, type RuckusFormValues } from './RuckusForm';
import { SilverPeakForm, type SilverPeakFormValues } from './SilverPeakForm';
import { VeloCloudForm, type VeloCloudFormValues } from './VeloCloudForm';
import { VENDOR_DIALOG_FORM_ID } from './VendorDialogContainer';
import { VersaForm, type VersaFormValues } from './VersaForm';
import { ViptelaForm, type ViptelaFormValues } from './ViptelaForm';
import type { VendorDialogType } from './constants';
import {
  defaultInitialValues,
  getRateLimitationSettingsDefaultValues,
} from './helpers/applyDefaultInitialValues';
import { deleteVendorApiTemporaryAttributes } from './helpers/deleteVendorsApiTemporaryAttributes';
import type { FormVendorValues, Vendor, VendorSubmit } from './vendorTypes';

interface Props {
  dialogType: VendorDialogType;
  editName?: string;
  initialValues?: Vendor;
  onClose: () => void;
  onSubmit: (values: VendorSubmit, type: VendorDialogType) => void;
  testingVendor: boolean;
  type: VendorApiType;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable, complex branching logic
export const VendorDialog: FC<Props> = React.memo((props) => {
  const [submittedByTest, setSubmittedByTest] = useState(false);
  const [type, setType] = useState<VendorApiType>(props.type);

  const getInitialValues = (): FormVendorValues => {
    if (props.initialValues) {
      return pipe(
        props.initialValues,
        deleteVendorApiTemporaryAttributes,
        (values) => {
          const defaultValues = defaultInitialValues[type];

          return {
            ...getRateLimitationSettingsDefaultValues(type),
            ...defaultValues,
            ...values,
          } as typeof defaultValues;
        },
      );
    }

    return {
      ...getRateLimitationSettingsDefaultValues(type),
      ...defaultInitialValues[type],
    };
  };

  const handleSubmit = (values: VendorSubmit) => {
    const { onSubmit } = props;

    onSubmit(
      produce(values, (draft) => {
        draft.type = props.dialogType === 'add' ? type : draft.type;
        draft.testOnly = submittedByTest;
      }),
      props.dialogType,
    );
  };

  const commonVendorFormProps = {
    onSubmit: handleSubmit,
    vendorDialogContainerPassthruProps: {
      setType,
      type,
      editName: props.editName,
    },
    dialogType: props.dialogType,
  };

  const title =
    props.dialogType === 'add' ? 'New Vendor API' : 'Edit Vendor API';

  return (
    <SidePanel
      title={title}
      closeOnBackdropClick={false}
      onClose={props.onClose}
      footer={
        <div className='flex w-full gap-xs'>
          <Button
            className='flex-1'
            dataTestId='vendors-save-button'
            disabled={props.testingVendor}
            form={VENDOR_DIALOG_FORM_ID}
            icon='fa-floppy-disk'
            onClick={() => {
              setSubmittedByTest(false);
            }}
            type='submit'
            variant='primary'
          >
            {props.dialogType === 'add' ? 'Save' : 'Update'}
          </Button>
          <Button
            className='flex-1'
            form={VENDOR_DIALOG_FORM_ID}
            loading={props.testingVendor}
            onClick={() => {
              setSubmittedByTest(true);
            }}
            type='submit'
            variant='secondary'
          >
            Test connection
          </Button>
          <Button
            className='flex-1'
            onClick={props.onClose}
            type='button'
            variant='secondary'
          >
            Close
          </Button>
        </div>
      }
    >
      {type === settingTypes.vendorApiNames.f5osa && (
        <F5osAForm
          initialValues={getInitialValues() as F5osAFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.f5osc && (
        <F5osCForm
          initialValues={getInitialValues() as F5osCFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.meraki && (
        <MerakiForm
          initialValues={getInitialValues() as MerakiFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.checkpoint && (
        <CheckpointApiForm
          initialValues={getInitialValues() as CheckPointFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.aws && (
        <AwsForm
          initialValues={getInitialValues() as AwsFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.versa && (
        <VersaForm
          initialValues={getInitialValues() as VersaFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.silverpeak && (
        <SilverPeakForm
          initialValues={getInitialValues() as SilverPeakFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.ruckusvsz && (
        <RuckusForm
          initialValues={getInitialValues() as RuckusFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.ciscofmc && (
        <CiscoFmcForm
          initialValues={getInitialValues() as CiscoFmcFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.nsxT && (
        <NsxTForm
          initialValues={getInitialValues() as NsxTFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.viptela && (
        <ViptelaForm
          initialValues={getInitialValues() as ViptelaFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.azure && (
        <AzureForm
          initialValues={getInitialValues() as AzureFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.junipermist && (
        <JuniperMistForm
          initialValues={getInitialValues() as JuniperMistFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.ciscoapic && (
        <CiscoApicForm
          initialValues={getInitialValues() as CiscoApicFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.prisma && (
        <PrismaForm
          initialValues={getInitialValues() as PrismaFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.forcepoint && (
        <ForcePointForm
          initialValues={getInitialValues() as ForcePointFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.gcp && (
        <GoogleCloudPlatformForm
          initialValues={getInitialValues() as GCPFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.velocloud && (
        <VeloCloudForm
          initialValues={getInitialValues() as VeloCloudFormValues}
          {...commonVendorFormProps}
        />
      )}
      {type === settingTypes.vendorApiNames.arubaNewCentral && (
        <ArubaNewCentralForm
          initialValues={getInitialValues() as ArubaNewCentralFormValues}
          {...commonVendorFormProps}
        />
      )}
    </SidePanel>
  );
});
